import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { AlertCircle, Send, Download } from 'lucide-react';
import { ReportHeader } from '../components/ReportHeader';
import { Sparkline } from '../components/Sparkline';
import { trailingMonths } from '../lib/snapshotRanges';

const API = process.env.REACT_APP_BACKEND_URL;

const UNAVAILABLE_MSG =
  'SCORM Cloud data is temporarily unavailable. This can happen if your account is not connected to the upstream LevelUp API. Please try again shortly or contact your administrator.';

const fmt = (n) => (n ?? 0).toLocaleString();
const fmtDT = (s) => (s ? String(s).replace('T', ' ').slice(0, 16) : '—');

function SABadge({ name }) {
  if (!name) return <span style={{ color: '#94a3b8' }}>—</span>;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#eef2ff', color: '#4338ca', whiteSpace: 'nowrap' }}>
      {name}
    </span>
  );
}

function StatusPill({ status }) {
  const done = String(status || '').toLowerCase() === 'completed';
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: done ? '#dcfce7' : '#f1f5f9', color: done ? '#15803d' : '#64748b' }}>
      {status || '—'}
    </span>
  );
}

function StatTiles({ tiles }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${tiles.length}, minmax(0,1fr))`, gap: 12, marginBottom: 16 }}>
      {tiles.map(t => (
        <div key={t.label} className="stat-card" data-testid={`scorm-tile-${t.key}`} style={{ padding: 16 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', fontWeight: 600 }}>{t.label}</div>
          <div className="stat-value" style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>{fmt(t.value)}</div>
        </div>
      ))}
    </div>
  );
}

function PaginationBar({ page, totalPages, total, perPage, onPage, testId }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, page + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  return (
    <div className="pagination" data-testid={testId}>
      <span className="pagination-info">{from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}</span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {pages.map(p => (
        <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

const ErrorBanner = ({ message }) => (
  <div data-testid="scorm-error" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '24px 20px' }}>
    <AlertCircle size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
    <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>{message}</p>
  </div>
);

const Spinner = () => (
  <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
);

// ── Executive snapshot bar (above tabs) ─────────────────────────────────────────
function SnapshotBar({ dateFrom, dateTo }) {
  const [snap, setSnap] = useState(null);
  const [series, setSeries] = useState(null);
  const reqId = useRef(0);

  // Tile values follow the page FY range.
  useEffect(() => {
    let cancelled = false;
    const myId = ++reqId.current;
    (async () => {
      try {
        const dp = {};
        if (dateFrom) dp.date_from = dateFrom;
        if (dateTo) dp.date_to = dateTo;
        const [pw, cw] = await Promise.all([
          axios.get(`${API}/api/lup/v2/report/scorm-cloud-enrollments/partner-wise`, { params: { page: 1, per_page: 500, ...dp }, withCredentials: true }),
          axios.get(`${API}/api/lup/v2/report/scorm-cloud-enrollments/course-wise`, { params: { page: 1, per_page: 1, ...dp }, withCredentials: true }),
        ]);
        if (cancelled || myId !== reqId.current) return;
        const partners = pw.data?.data || [];
        const topPartner = partners.reduce((a, b) => ((b.enrollments || 0) > (a?.enrollments || 0) ? b : a), null);
        const topCourse = (cw.data?.data || [])[0] || null;
        const enr = pw.data?.total_enrollments || 0;
        const cmp = pw.data?.total_completions || 0;
        setSnap({
          enrollments: enr,
          completions: cmp,
          rate: enr > 0 ? Math.round((cmp / enr) * 1000) / 10 : 0,
          partners: pw.data?.total_partners || 0,
          lms: pw.data?.total_destination_lms || 0,
          courses: cw.data?.total_courses || 0,
          topPartner, topCourse,
        });
      } catch (e) {
        if (!cancelled && myId === reqId.current) setSnap(null); // hidden gracefully when upstream unavailable
      }
    })();
    return () => { cancelled = true; };
  }, [dateFrom, dateTo]);

  // Period-over-period trend — last 6 calendar months (fixed, mount-only).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const months = trailingMonths(6);
        const res = await Promise.all(months.map(m =>
          axios.get(`${API}/api/lup/v2/report/scorm-cloud-enrollments/partner-wise`, { params: { page: 1, per_page: 1, date_from: m.from, date_to: m.to }, withCredentials: true })
        ));
        if (cancelled) return;
        setSeries({
          enrollments: res.map(r => r.data?.total_enrollments || 0),
          completions: res.map(r => r.data?.total_completions || 0),
        });
      } catch (e) {
        if (!cancelled) setSeries(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const BigTile = ({ k, label, value, sub, spark }) => (
    <div className="stat-card" data-testid={`scorm-snapshot-${k}`} style={{ padding: 16, minWidth: 0 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', fontWeight: 600 }}>{label}</div>
      <div className="stat-value" style={{ fontSize: value && value.length > 14 ? 16 : 22, fontWeight: 800, color: '#0f172a', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value}>{value}</div>
      {sub != null && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{sub}</div>}
      {spark && <Sparkline values={spark} />}
    </div>
  );

  return (
    <div data-testid="scorm-snapshot-bar" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#94a3b8', fontWeight: 700 }}>SCORM Cloud Snapshot</div>
        {series && <div style={{ fontSize: 11, color: '#94a3b8' }}>Trend: last 6 months</div>}
      </div>
      {!snap ? (
        <div className="stat-card" style={{ padding: 16, color: '#94a3b8', fontSize: 13 }}>Snapshot data unavailable for the selected range.</div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 12 }}>
        <BigTile k="enrollments" label="Total Enrollments" value={fmt(snap.enrollments)} sub={`${fmt(snap.courses)} courses · ${fmt(snap.lms)} LMS`} spark={series?.enrollments} />
        <BigTile k="completions" label="Total Completions" value={fmt(snap.completions)} sub={`${snap.rate}% completion rate`} spark={series?.completions} />
        <BigTile k="partners" label="Partners" value={fmt(snap.partners)} sub="syndication partners" />
        <BigTile k="top-partner" label="Top Partner" value={snap.topPartner?.partner_name || '—'} sub={snap.topPartner ? `${fmt(snap.topPartner.enrollments)} enrollments` : null} />
        <BigTile k="top-course" label="Top Course" value={snap.topCourse?.course_name || '—'} sub={snap.topCourse ? `${fmt(snap.topCourse.enrollments)} enrollments` : null} />
      </div>
      )}
    </div>
  );
}

// ── Tab 1: All Enrollments (API 3) + CSV export (API 7) ─────────────────────────
function AllEnrollmentsTab({ dateFrom, dateTo }) {
  const [searchInput, setSearchInput] = useState('');
  const [applied, setApplied] = useState({ search: '', sort: 'completed_at:desc' });
  const [sort, setSort] = useState('completed_at:desc');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, last_page: 1, total_enrollments: 0, total_completions: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [exporting, setExporting] = useState(false);
  const PER_PAGE = 100;

  const fetchData = useCallback(async (p, f) => {
    setLoading(true); setErrorMsg(null);
    try {
      const params = { page: p, per_page: PER_PAGE, sort: f.sort };
      if (f.search) params.search = f.search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(`${API}/api/lup/v2/report/scorm-cloud-enrollments`, { params, withCredentials: true });
      setRows(r.data?.data || []);
      setMeta({
        total: r.data?.total || 0, last_page: r.data?.last_page || 1,
        total_enrollments: r.data?.total_enrollments || 0, total_completions: r.data?.total_completions || 0,
      });
      setPage(r.data?.current_page || p);
    } catch (e) {
      setErrorMsg(UNAVAILABLE_MSG); setRows([]); setMeta({ total: 0, last_page: 1, total_enrollments: 0, total_completions: 0 });
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(1, applied); }, [fetchData, applied]);

  const apply = () => setApplied({ search: searchInput, sort });
  const reset = () => { setSearchInput(''); setSort('completed_at:desc'); setApplied({ search: '', sort: 'completed_at:desc' }); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { sort: applied.sort };
      if (applied.search) params.search = applied.search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(`${API}/api/lup/v2/report/scorm-cloud-enrollments/export`, { params, withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'scorm-cloud-enrollments.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed — SCORM Cloud data is temporarily unavailable.');
    } finally { setExporting(false); }
  };

  return (
    <div data-testid="scorm-all-tab">
      <StatTiles tiles={[
        { key: 'enrollments', label: 'Total Enrollments', value: meta.total_enrollments },
        { key: 'completions', label: 'Total Completions', value: meta.total_completions },
      ]} />
      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group">
          <label className="filter-label">Search</label>
          <input type="text" className="filter-input" placeholder="Learner, course, MS ID, partner, LMS…"
            value={searchInput} onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && apply()} data-testid="scorm-all-search" />
        </div>
        <div className="filter-group">
          <label className="filter-label">Sort</label>
          <select className="filter-select" value={sort} onChange={e => setSort(e.target.value)} data-testid="scorm-all-sort">
            <option value="completed_at:desc">Completed (newest)</option>
            <option value="completed_at:asc">Completed (oldest)</option>
            <option value="enrolled_at:desc">Enrolled (newest)</option>
            <option value="enrolled_at:asc">Enrolled (oldest)</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={apply} data-testid="scorm-all-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={reset} data-testid="scorm-all-reset-btn">Reset</button>
          <button className="btn btn-secondary" onClick={exportCsv} disabled={exporting} data-testid="scorm-all-export-btn" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={14} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      <div className="table-container">
        {loading ? <Spinner /> : errorMsg ? <ErrorBanner message={errorMsg} /> : rows.length === 0 ? (
          <div className="empty-state"><h3>No enrollments found</h3><p>Try adjusting your filters.</p></div>
        ) : (
          <>
            <div className="table-header">
              <span className="table-title">All Enrollments</span>
              <span className="table-count" style={{ marginLeft: 8 }}>({fmt(meta.total)} rows)</span>
            </div>
            <div className="table-scroll">
              <table data-testid="scorm-all-table">
                <thead>
                  <tr>
                    <th>Learner</th><th>Course</th><th>MS Course ID</th><th>Solution Area</th>
                    <th>Partner</th><th>Destination LMS</th><th>Micro</th><th>Status</th>
                    <th>Enrolled</th><th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={`${row.learner_id}-${i}`} data-testid={`scorm-all-row-${i}`}>
                      <td style={{ fontSize: 11, fontFamily: 'monospace', color: '#475569', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.learner_id}</td>
                      <td style={{ fontSize: 13 }}>{row.course_title || '—'}</td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{row.ms_course_id || '—'}</td>
                      <td><SABadge name={row.solution_area} /></td>
                      <td style={{ fontSize: 12 }}>{row.partner_name || '—'}</td>
                      <td style={{ fontSize: 12 }}>{row.destination_lms || '—'}</td>
                      <td style={{ fontSize: 12 }}>{row.microskilling ? 'Yes' : 'No'}</td>
                      <td><StatusPill status={row.status} /></td>
                      <td style={{ fontSize: 12 }}>{fmtDT(row.enrolled_at)}</td>
                      <td style={{ fontSize: 12 }}>{fmtDT(row.completed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={meta.last_page} total={meta.total} perPage={PER_PAGE} onPage={p => fetchData(p, applied)} testId="scorm-all-pagination" />
          </>
        )}
      </div>
    </div>
  );
}

// ── Tab 2: By Partner (API 4) ───────────────────────────────────────────────────
function ByPartnerTab({ options, dateFrom, dateTo }) {
  const [solutionArea, setSolutionArea] = useState('');
  const [microskilling, setMicroskilling] = useState('');
  const [msCourseId, setMsCourseId] = useState('');
  const [applied, setApplied] = useState({});
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, last_page: 1, total_partners: 0, total_destination_lms: 0, total_enrollments: 0, total_completions: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const PER_PAGE = 25;

  const fetchData = useCallback(async (p, f) => {
    setLoading(true); setErrorMsg(null);
    try {
      const params = { page: p, per_page: PER_PAGE };
      if (f.solution_area) params.solution_area = f.solution_area;
      if (f.microskilling) params.microskilling = f.microskilling;
      if (f.ms_course_id) params.ms_course_id = f.ms_course_id;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(`${API}/api/lup/v2/report/scorm-cloud-enrollments/partner-wise`, { params, withCredentials: true });
      setRows(r.data?.data || []);
      setMeta({
        total: r.data?.total || 0, last_page: r.data?.last_page || 1,
        total_partners: r.data?.total_partners || 0, total_destination_lms: r.data?.total_destination_lms || 0,
        total_enrollments: r.data?.total_enrollments || 0, total_completions: r.data?.total_completions || 0,
      });
      setPage(r.data?.current_page || p);
    } catch (e) {
      setErrorMsg(UNAVAILABLE_MSG); setRows([]);
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(1, applied); }, [fetchData, applied]);

  const apply = () => setApplied({ solution_area: solutionArea, microskilling, ms_course_id: msCourseId });
  const reset = () => { setSolutionArea(''); setMicroskilling(''); setMsCourseId(''); setApplied({}); };

  return (
    <div data-testid="scorm-partner-tab">
      <StatTiles tiles={[
        { key: 'partners', label: 'Partners', value: meta.total_partners },
        { key: 'lms', label: 'Destination LMS', value: meta.total_destination_lms },
        { key: 'penrollments', label: 'Total Enrollments', value: meta.total_enrollments },
        { key: 'pcompletions', label: 'Total Completions', value: meta.total_completions },
      ]} />
      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group">
          <label className="filter-label">Solution Area</label>
          <select className="filter-select" value={solutionArea} onChange={e => setSolutionArea(e.target.value)} data-testid="scorm-partner-solution-area">
            <option value="">All</option>
            {(options.solution_areas || []).map(sa => <option key={sa} value={sa}>{sa}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Microskilling</label>
          <select className="filter-select" value={microskilling} onChange={e => setMicroskilling(e.target.value)} data-testid="scorm-partner-microskilling">
            <option value="">All</option><option value="true">Yes</option><option value="false">No</option>
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">MS Course ID</label>
          <input type="text" className="filter-input" value={msCourseId} onChange={e => setMsCourseId(e.target.value)} onKeyDown={e => e.key === 'Enter' && apply()} data-testid="scorm-partner-ms-course" />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={apply} data-testid="scorm-partner-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={reset} data-testid="scorm-partner-reset-btn">Reset</button>
        </div>
      </div>

      <div className="table-container">
        {loading ? <Spinner /> : errorMsg ? <ErrorBanner message={errorMsg} /> : rows.length === 0 ? (
          <div className="empty-state"><h3>No partners found</h3><p>Try adjusting your filters.</p></div>
        ) : (
          <>
            <div className="table-header">
              <span className="table-title">Enrollments by Partner</span>
              <span className="table-count" style={{ marginLeft: 8 }}>({fmt(meta.total)} rows)</span>
            </div>
            <div className="table-scroll">
              <table data-testid="scorm-partner-table">
                <thead>
                  <tr>
                    <th>Partner</th><th>Destination LMS</th>
                    <th className="td-right">Courses Syndicated</th><th className="td-right">Learners</th>
                    <th className="td-right">Enrollments</th><th className="td-right">Completions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={`${row.partner_name}-${row.destination_lms}-${i}`} data-testid={`scorm-partner-row-${i}`}>
                      <td style={{ fontSize: 13, fontWeight: 500 }}>{row.partner_name || '—'}</td>
                      <td style={{ fontSize: 12 }}>{row.destination_lms || '—'}</td>
                      <td className="td-right">{fmt(row.courses_syndicated)}</td>
                      <td className="td-right">{fmt(row.learners)}</td>
                      <td className="td-right" style={{ fontWeight: 600 }}>{fmt(row.enrollments)}</td>
                      <td className="td-right" style={{ fontWeight: 600 }}>{fmt(row.completions)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={meta.last_page} total={meta.total} perPage={PER_PAGE} onPage={p => fetchData(p, applied)} testId="scorm-partner-pagination" />
          </>
        )}
      </div>
    </div>
  );
}

// ── Tab 3: By Course (API 5) ────────────────────────────────────────────────────
function ByCourseTab({ options, dateFrom, dateTo }) {
  const [searchInput, setSearchInput] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [destinationLms, setDestinationLms] = useState('');
  const [solutionArea, setSolutionArea] = useState('');
  const [microskilling, setMicroskilling] = useState('');
  const [applied, setApplied] = useState({});
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, last_page: 1, total_courses: 0, total_enrollments: 0, total_completions: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const PER_PAGE = 25;

  const fetchData = useCallback(async (p, f) => {
    setLoading(true); setErrorMsg(null);
    try {
      const params = { page: p, per_page: PER_PAGE };
      if (f.search) params.search = f.search;
      if (f.partner_name) params.partner_name = f.partner_name;
      if (f.destination_lms) params.destination_lms = f.destination_lms;
      if (f.solution_area) params.solution_area = f.solution_area;
      if (f.microskilling) params.microskilling = f.microskilling;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(`${API}/api/lup/v2/report/scorm-cloud-enrollments/course-wise`, { params, withCredentials: true });
      setRows(r.data?.data || []);
      setMeta({
        total: r.data?.total || 0, last_page: r.data?.last_page || 1,
        total_courses: r.data?.total_courses || 0,
        total_enrollments: r.data?.total_enrollments || 0, total_completions: r.data?.total_completions || 0,
      });
      setPage(r.data?.current_page || p);
    } catch (e) {
      setErrorMsg(UNAVAILABLE_MSG); setRows([]);
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(1, applied); }, [fetchData, applied]);

  const apply = () => setApplied({ search: searchInput, partner_name: partnerName, destination_lms: destinationLms, solution_area: solutionArea, microskilling });
  const reset = () => { setSearchInput(''); setPartnerName(''); setDestinationLms(''); setSolutionArea(''); setMicroskilling(''); setApplied({}); };

  return (
    <div data-testid="scorm-course-tab">
      <StatTiles tiles={[
        { key: 'courses', label: 'Courses', value: meta.total_courses },
        { key: 'cenrollments', label: 'Total Enrollments', value: meta.total_enrollments },
        { key: 'ccompletions', label: 'Total Completions', value: meta.total_completions },
      ]} />
      <div className="filter-bar" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="filter-group">
          <label className="filter-label">Search</label>
          <input type="text" className="filter-input" placeholder="Course, MS ID, partner, LMS…" value={searchInput} onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && apply()} data-testid="scorm-course-search" />
        </div>
        <div className="filter-group">
          <label className="filter-label">Partner</label>
          <select className="filter-select" value={partnerName} onChange={e => setPartnerName(e.target.value)} data-testid="scorm-course-partner">
            <option value="">All</option>
            {(options.partners || []).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Destination LMS</label>
          <select className="filter-select" value={destinationLms} onChange={e => setDestinationLms(e.target.value)} data-testid="scorm-course-lms">
            <option value="">All</option>
            {(options.destination_lms || []).map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Solution Area</label>
          <select className="filter-select" value={solutionArea} onChange={e => setSolutionArea(e.target.value)} data-testid="scorm-course-solution-area">
            <option value="">All</option>
            {(options.solution_areas || []).map(sa => <option key={sa} value={sa}>{sa}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Microskilling</label>
          <select className="filter-select" value={microskilling} onChange={e => setMicroskilling(e.target.value)} data-testid="scorm-course-microskilling">
            <option value="">All</option><option value="true">Yes</option><option value="false">No</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={apply} data-testid="scorm-course-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={reset} data-testid="scorm-course-reset-btn">Reset</button>
        </div>
      </div>

      <div className="table-container">
        {loading ? <Spinner /> : errorMsg ? <ErrorBanner message={errorMsg} /> : rows.length === 0 ? (
          <div className="empty-state"><h3>No courses found</h3><p>Try adjusting your filters.</p></div>
        ) : (
          <>
            <div className="table-header">
              <span className="table-title">Enrollments by Course</span>
              <span className="table-count" style={{ marginLeft: 8 }}>({fmt(meta.total)} rows)</span>
            </div>
            <div className="table-scroll">
              <table data-testid="scorm-course-table">
                <thead>
                  <tr>
                    <th>Course</th><th>MS Course ID</th><th>Solution Area</th><th>Micro</th>
                    <th className="td-right">Enrollments</th><th className="td-right">Completions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={`${row.course_id}-${i}`} data-testid={`scorm-course-row-${i}`}>
                      <td style={{ fontSize: 13, fontWeight: 500 }}>{row.course_name || '—'}</td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{row.ms_course_id || '—'}</td>
                      <td><SABadge name={row.solution_area} /></td>
                      <td style={{ fontSize: 12 }}>{row.microskilling ? 'Yes' : 'No'}</td>
                      <td className="td-right" style={{ fontWeight: 600 }}>{fmt(row.enrollments)}</td>
                      <td className="td-right" style={{ fontWeight: 600 }}>{fmt(row.completions)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={meta.last_page} total={meta.total} perPage={PER_PAGE} onPage={p => fetchData(p, applied)} testId="scorm-course-pagination" />
          </>
        )}
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────────
export default function ScormCloudActivity() {
  const [tab, setTab] = useState('all');
  const [options, setOptions] = useState({ partners: [], solution_areas: [], destination_lms: [] });
  const [period, setPeriod] = useState({ startDate: '', endDate: '' });
  const handlePeriod = useCallback(({ startDate, endDate }) => setPeriod({ startDate: startDate || '', endDate: endDate || '' }), []);

  useEffect(() => {
    axios.get(`${API}/api/lup/v2/report/scorm-cloud-enrollments/filters`, { withCredentials: true })
      .then(r => setOptions({
        partners: r.data?.partners || [],
        solution_areas: r.data?.solution_areas || [],
        destination_lms: r.data?.destination_lms || [],
      }))
      .catch(() => setOptions({ partners: [], solution_areas: [], destination_lms: [] }));
  }, []);

  const tabs = [
    { key: 'all', label: 'All Enrollments' },
    { key: 'partner', label: 'By Partner' },
    { key: 'course', label: 'By Course' },
  ];

  return (
    <div data-testid="scorm-cloud-activity-page">
      <ReportHeader reportTitle="SCORM Cloud" onChange={handlePeriod} />
      <div className="page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Send size={22} color="#4338ca" /> SCORM Cloud Activity
        </h1>
        <p>SCORM Cloud syndicated-course enrollment activity — overall, by partner and by course — sourced live from the LevelUp reporting API.</p>
      </div>

      <SnapshotBar dateFrom={period.startDate} dateTo={period.endDate} />

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e2e8f0', margin: '0 0 16px' }}>
        {tabs.map(t => (
          <button key={t.key} data-testid={`scorm-tab-${t.key}`} onClick={() => setTab(t.key)}
            style={{ padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              color: tab === t.key ? '#4338ca' : '#64748b',
              borderBottom: tab === t.key ? '2px solid #4338ca' : '2px solid transparent', marginBottom: -1 }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'all' && <AllEnrollmentsTab dateFrom={period.startDate} dateTo={period.endDate} />}
      {tab === 'partner' && <ByPartnerTab options={options} dateFrom={period.startDate} dateTo={period.endDate} />}
      {tab === 'course' && <ByCourseTab options={options} dateFrom={period.startDate} dateTo={period.endDate} />}
    </div>
  );
}
